"use client";

import { useState } from "react";
import Link from "next/link";
import { ManualLogSheet } from "@/app/_components/companion/ManualLogSheet";
import { RecentSessions } from "@/app/_components/companion/RecentSessions";
import { manualLogSheetView } from "@/app/_components/companion/manual-log-state";
import { trackEvent } from "@/lib/analytics";
import {
  manualLogStartedEvent,
  trackingStartedEvent,
  type CompanionAnalyticsEvent,
} from "@/lib/companion/analytics-events";
import type { CompanionHomeModel } from "@/lib/companion/home-model";
import { CompanionFeedbackPanel } from "../CompanionFeedbackPanel";
import { recordCardView } from "../companion-home-state";
import type { CompanionWorkspaceProps } from "./companion-workspace-types";

export function CompanionRecordWorkspace({
  data,
  model,
}: CompanionWorkspaceProps) {
  const feedbackPlace = model.groundTruthPromptPlace ?? model.visiblePlace;

  return (
    <div className="space-y-5">
      <RecordCard
        model={model}
        record={model.record}
        showWrappedLink={false}
      />
      <CompanionRecordLinks record={model.record} />
      <RecentSessions />
      <CompanionFeedbackPanel
        place={feedbackPlace}
        isLoggedIn={Boolean(data.user)}
        heading={model.groundTruthPromptPlace ? "How was it really?" : "Ground truth"}
        kicker={model.groundTruthPromptPlace ? "Ground truth" : "Forecast check"}
      />
    </div>
  );
}

function CompanionRecordLinks({
  record,
}: {
  record: CompanionHomeModel["record"];
}) {
  return (
    <section
      aria-labelledby="record-links-heading"
      className="rounded-md border border-border-light bg-white p-4 md:p-5"
    >
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
        Keep exploring
      </p>
      <h2 id="record-links-heading" className="font-serif text-[26px] leading-tight text-navy">
        Your season record
      </h2>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          href="/companion/history"
          className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white no-underline hover:bg-navy/90"
        >
          Review history
        </Link>
        <Link
          href="/companion/passport"
          className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy no-underline hover:border-gold/50"
        >
          Open Passport
        </Link>
        {record.wrappedCards > 0 ? (
          <Link
            href="/companion/wrapped"
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy no-underline hover:border-gold/50"
          >
            Open Wrapped
          </Link>
        ) : null}
      </div>
    </section>
  );
}

export function RecordCard({
  model,
  record,
  showWrappedLink = true,
}: {
  model: CompanionHomeModel;
  record: CompanionHomeModel["record"];
  showWrappedLink?: boolean;
}) {
  const [showLogShell, setShowLogShell] = useState(false);
  const view = recordCardView(record);
  const sheet = manualLogSheetView(model);

  return (
    <section aria-labelledby="record-heading" className="rounded-md border border-border-light bg-white p-4 md:p-5">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
        Your record
      </p>
      <h2 id="record-heading" className="font-serif text-[26px] leading-tight text-navy">
        Private proof
      </h2>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {view.tiles.map((tile) => (
          <Metric
            key={tile.label}
            label={tile.label}
            value={tile.value}
            subLabel={tile.subLabel ?? null}
          />
        ))}
      </div>
      <p className="mt-3 text-[12.5px] leading-relaxed text-text-muted">
        {view.body}
      </p>
      {view.groundTruthContribution ? (
        <p className="mt-2 text-[12.5px] font-semibold text-navy">
          {view.groundTruthContribution}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        {view.actions.map((action, index) => (
          action.kind === "link" ? (
            <Link
              key={action.label}
              href={action.href}
              onClick={() => trackCompanionRecordAction(trackingStartedEvent())}
              className={
                index === 0
                  ? "inline-flex min-h-[44px] items-center justify-center rounded-sm bg-navy px-3 py-2 text-[12px] font-semibold text-white no-underline hover:bg-navy/90"
                  : "inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy no-underline hover:border-gold/50"
              }
            >
              {action.label}
            </Link>
          ) : (
            <button
              key={action.label}
              type="button"
              aria-expanded={showLogShell}
              aria-controls="record-log-shell"
              onClick={() => {
                if (!showLogShell) {
                  trackCompanionRecordAction(manualLogStartedEvent());
                }
                setShowLogShell((current) => !current);
              }}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy no-underline hover:border-gold/50"
            >
              {action.label}
            </button>
          )
        ))}
      </div>
      {showLogShell ? (
        <ManualLogSheet
          id="record-log-shell"
          view={sheet}
          title={view.logShell.title}
          body={view.logShell.body}
          className="mt-3 rounded-sm border border-border-light bg-warm-bg px-3 py-3"
        />
      ) : null}
      <div className="mt-4 border-t border-border-light pt-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">
            Trophy shelf
          </p>
          <Link
            href={view.trophyShelf.allTrophiesHref}
            className="inline-flex min-h-[44px] items-center text-[12px] font-semibold text-navy no-underline hover:text-gold"
          >
            All trophies
          </Link>
        </div>
        {view.trophyShelf.latest.length > 0 ? (
          <ul className="mt-1 divide-y divide-border-light border-t border-border-light">
            {view.trophyShelf.latest.map((trophy) => (
              <li
                key={trophy.label}
                className="flex min-h-[44px] items-center justify-between gap-3 py-2"
              >
                <span className="min-w-0 truncate text-[13px] font-semibold text-navy">
                  {trophy.label}
                </span>
                {trophy.isNew ? (
                  <span className="shrink-0 rounded-full bg-gold/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-[1px] text-navy">
                    New trophy
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : view.trophyShelf.emptyCopy ? (
          <p className="mt-3 text-[12px] leading-relaxed text-text-muted">
            {view.trophyShelf.emptyCopy}
          </p>
        ) : null}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {showWrappedLink && view.wrappedLink ? (
          <Link
            href={view.wrappedLink.href}
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy no-underline hover:border-gold/50"
          >
            {view.wrappedLink.label}
          </Link>
        ) : null}
        {view.leaderboardCard ? (
          <Link
            href={view.leaderboardCard.href}
            className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-border bg-white px-3 py-2 text-[12px] font-semibold text-navy no-underline hover:border-gold/50"
          >
            Leaderboards
          </Link>
        ) : null}
      </div>
      {view.leaderboardCard ? (
        <p className="mt-3 text-[12px] leading-relaxed text-text-muted">
          {view.leaderboardCard.body}
        </p>
      ) : null}
    </section>
  );
}

function trackCompanionRecordAction(event: CompanionAnalyticsEvent): void {
  try {
    trackEvent(event.name, event.properties);
  } catch {
    // Analytics must never interrupt Record actions.
  }
}

function Metric({
  label,
  value,
  subLabel = null,
}: {
  label: string;
  value: string;
  subLabel?: string | null;
}) {
  return (
    <div className="rounded-sm bg-warm-bg px-3 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[1.2px] text-text-muted">
        {label}
      </p>
      <p className="mt-1 font-serif text-[28px] leading-none text-navy">{value}</p>
      {subLabel ? (
        <p className="mt-1 text-[11px] font-semibold text-text-muted">{subLabel}</p>
      ) : null}
    </div>
  );
}
